import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, RefreshCw, Trash2 } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";
import { Vide } from "@/components/commun";
import { ImportProspection } from "@/components/prospection/ImportProspection";
import { ChoixListe } from "@/pages/parametres/ChoixListe";
import { confirmer } from "@/lib/confirmer";

const Pill = ({ actif, onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId} className="min-h-[44px] px-4 at-pill font-semibold whitespace-nowrap"
    style={{ background: actif ? "var(--accent)" : "var(--surface)", color: actif ? "var(--accent-ink)" : "var(--ink)" }}>{children}</button>
);

const Chargement = ({ erreur, testId }) => <p className="at-muted" data-testid={`${testId}-${erreur ? "error" : "loading"}`}>{erreur || "Chargement…"}</p>;

const Fichiers = ({ cle }) => {
  const [liste, setListe] = useState(null);
  const [err, setErr] = useState(null);
  const charger = useCallback(() => api.get("/prospection/fichiers").then((r) => setListe(r.data)).catch((e) => setErr(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger, cle]);
  const suppr = (f) => confirmer(`Mettre « ${f.nom} » et ses contacts à la corbeille ?`).then((ok) => ok && api.delete(`/prospection/fichiers/${f.id}`).then(() => { toast.success("Fichier mis à la corbeille."); charger(); }).catch((e) => toast.error(messageErreur(e))));
  if (!liste) return <Chargement erreur={err} testId="files" />;
  if (!liste.length) return <Vide texte="Aucun fichier de prospection." testId="files-empty" />;
  return (
    <div className="grid md:grid-cols-2 gap-3" data-testid="files-list">
      {liste.map((f) => (
        <section key={f.id} className="at-card p-4 space-y-3" data-testid={`file-card-${f.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0"><p className="font-bold truncate">{f.nom}</p><p className="text-xs at-muted">{formatDateHeure(f.created_at)}{f.type === "recyclage" ? " · Recyclage" : f.type === "auto" ? " · Import automatique" : ""}</p></div>
            <button type="button" className="at-icon-btn shrink-0" aria-label="Corbeille" onClick={() => suppr(f)} data-testid={`file-trash-${f.id}`}><Trash2 size={16} /></button>
          </div>
          <div className="flex h-3 at-pill overflow-hidden" style={{ background: "var(--surface-2)" }} data-testid={`file-bar-${f.id}`}>
            {f.repartition.map((r) => <span key={r.code} style={{ flex: r.nombre, background: r.couleur }} title={`${r.nom} : ${r.nombre}`} />)}
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-xs">
            {[["Contacts", f.nb_contacts, "count"], ["À contacter", f.a_contacter, "todo"], ["Intéressés", f.interesses, "won"], ["À rappeler", f.a_rappeler, "callback"], ["Tentatives moy.", f.tentatives_moyennes, "tries"], ["Agents", f.agents.length, "agents"]].map(([l, v, t]) => (
              <div key={t} className="at-soft p-2"><p className="at-muted">{l}</p><p className="font-bold text-base" data-testid={`file-${t}-${f.id}`}>{v}</p></div>
            ))}
          </div>
          {f.agents.length > 0 && <p className="text-xs at-muted truncate">{f.agents.join(", ")}</p>}
        </section>
      ))}
    </div>
  );
};

const Distribution = () => {
  const [d, setD] = useState(null);
  const [adj, setAdj] = useState({});
  const [err, setErr] = useState(null);
  const charger = useCallback(() => api.get("/prospection/distribution").then((r) => setD(r.data)).catch((e) => setErr(messageErreur(e))), []);
  useEffect(() => { charger(); }, [charger]);
  const relancer = () => api.post("/prospection/distribution/relancer", { ajustements: Object.keys(adj).length ? adj : null })
    .then((r) => { toast.success(`Distribution faite : ${r.data.plan.reduce((s, p) => s + p.a_recevoir, 0)} contact(s).`); setAdj({}); charger(); }).catch((e) => toast.error(messageErreur(e)));
  const dose = (id, v) => api.patch(`/prospection/agents/${id}`, { dose_jour: Number(v) }).then(charger).catch((e) => toast.error(messageErreur(e)));
  if (!d) return <Chargement erreur={err} testId="dist" />;
  return (
    <div className="space-y-3" data-testid="distribution-view">
      <p className="at-muted text-sm">Stock disponible : <b data-testid="dist-stock">{d.stock_disponible}</b>{d.derniere ? ` · Dernière distribution ${formatDateHeure(d.derniere.date)} (${d.derniere.par})` : ""}</p>
      {d.plan.map((p) => (
        <div key={p.agent_id} className="at-card p-4 space-y-2" data-testid={`dist-agent-${p.agent_id}`}>
          <div className="flex items-center justify-between gap-2"><p className="font-bold">{p.agent}</p><p className="text-sm font-bold" data-testid={`dist-receive-${p.agent_id}`}>+{p.a_recevoir}</p></div>
          <p className="text-xs at-muted">Déjà en liste : {p.deja_en_liste} · frais : {p.frais} · {Object.entries(p.regions).map(([r, n]) => `${r} ${n}`).join(", ") || "aucune région"}</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="at-label block mb-1">Dose habituelle</span><input key={p.dose} type="number" min="0" defaultValue={p.dose} onBlur={(e) => Number(e.target.value) !== p.dose && dose(p.agent_id, e.target.value)} className="at-input" data-testid={`dist-dose-${p.agent_id}`} /></label>
            <label className="block"><span className="at-label block mb-1">Aujourd'hui seulement</span><input type="number" min="0" value={adj[p.agent_id] ?? ""} onChange={(e) => setAdj({ ...adj, [p.agent_id]: e.target.value })} placeholder={String(p.dose)} className="at-input" data-testid={`dist-adjust-${p.agent_id}`} /></label>
          </div>
          <p className="at-label">Objectifs du jour</p>
          <div className="grid grid-cols-3 gap-2">
            {[["objectif_appels", "Appels"], ["objectif_decroches", "Décrochés"], ["objectif_opportunites", "Opport."]].map(([k, l]) => {
              const v = d.objectifs?.[p.agent_id]?.[k];
              return <label key={k} className="block min-w-0"><span className="text-xs at-muted block mb-1">{l}</span><input key={`${k}-${v}`} type="number" min="0" defaultValue={v} onBlur={(e) => e.target.value !== "" && Number(e.target.value) !== v && api.patch(`/prospection/agents/${p.agent_id}`, { [k]: Number(e.target.value) }).then(() => { toast.success("Objectif enregistré."); charger(); }).catch((er) => toast.error(messageErreur(er)))} className="at-input" data-testid={`dist-goal-${k}-${p.agent_id}`} /></label>;
            })}
          </div>
          <ChoixListe label="Région préférée" valeur={d.preferences?.[p.agent_id] || "_"} onChange={(v) => api.patch(`/prospection/agents/${p.agent_id}`, { region_preferee: v === "_" ? "" : v }).then(charger)}
            options={[{ code: "_", label: "Aucune" }, ...(d.regions || []).map((r) => ({ code: r, label: r }))]} testId={`dist-region-${p.agent_id}`} />
        </div>
      ))}
      {d.absents.length > 0 && <p className="text-sm at-soft p-3" data-testid="dist-absents">Absents depuis 48 h (contacts rendus à la réserve) : {d.absents.map((a) => `${a.agent} (${a.contacts_rendus})`).join(", ")}</p>}
      <button type="button" className="at-btn at-btn-primary w-full" onClick={relancer} data-testid="dist-run"><RefreshCw size={16} />Lancer / relancer la distribution du jour</button>
    </div>
  );
};

const PAGE_GR = 50;

const RejetFaux = ({ apres }) => {
  const [ap, setAp] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const voir = () => { setOccupe(true); api.get("/prospection/regroupements-faux").then((r) => setAp(r.data)).catch((e) => toast.error(messageErreur(e))).finally(() => setOccupe(false)); };
  const appliquer = () => confirmer(`Rejeter ${ap.a_rejeter} faux regroupement(s) ? Rien n'est validé : les ${ap.restants} autres restent à valider.`, { danger: true, ok: "Rejeter" })
    .then((ok) => ok && api.post("/prospection/regroupements-faux").then((r) => { toast.success(r.data.message); setAp(null); apres(); }).catch((e) => toast.error(messageErreur(e))));
  return (
    <div className="at-card p-4 space-y-2" data-testid="groups-fake">
      <button type="button" className="at-btn at-btn-ghost w-full" onClick={voir} disabled={occupe} data-testid="groups-fake-preview">{occupe ? "Analyse…" : "Rejeter les faux regroupements"}</button>
      {ap && (
        <div className="space-y-1 text-sm" data-testid="groups-fake-summary">
          <p><b>{ap.a_rejeter}</b> à rejeter sur {ap.a_valider} · {ap.restants} restent à valider</p>
          {ap.motifs.map((m) => <p key={m.motif} className="at-soft px-3 py-1 flex justify-between gap-2" data-testid="groups-fake-motif"><span>{m.motif}</span><b>{m.nombre}</b></p>)}
          {ap.a_rejeter > 0 && <button type="button" className="at-btn at-btn-primary w-full" onClick={appliquer} data-testid="groups-fake-apply">Rejeter ces {ap.a_rejeter} regroupements</button>}
        </div>
      )}
    </div>
  );
};

const Regroupements = () => {
  const [liste, setListe] = useState([]);
  const [info, setInfo] = useState({ total: 0, raisons: [] });
  const [decalage, setDecalage] = useState(0);
  const [raison, setRaison] = useState("");
  const charger = useCallback(() => api.get("/prospection/regroupements", { params: { decalage, limite: PAGE_GR, ...(raison ? { raison } : {}) } })
    .then((r) => { setListe(r.data.elements); setInfo(r.data); }), [decalage, raison]);
  useEffect(() => { charger(); }, [charger]);
  const detecter = () => api.post("/prospection/regroupements/detecter").then((r) => { toast.success(`${r.data.proposes} regroupement(s) proposé(s).`); charger(); });
  const traiter = (g, v) => api.post(`/prospection/regroupements/${g.id}`, { valider: v }).then(() => { toast.success(v ? "Regroupement validé." : "Regroupement rejeté."); charger(); });
  const fin = Math.min(decalage + PAGE_GR, info.total);
  return (
    <div className="space-y-3" data-testid="groups-view">
      <button type="button" className="at-btn at-btn-ghost w-full" onClick={detecter} data-testid="groups-detect">Rechercher les propriétaires à plusieurs biens</button>
      <RejetFaux apres={() => { setDecalage(0); charger(); }} />
      <div className="flex flex-wrap gap-2" data-testid="groups-reasons">
        {[{ raison: "", nombre: info.raisons.reduce((s, x) => s + x.nombre, 0) }, ...info.raisons].map((x) => (
          <Pill key={x.raison || "tout"} actif={raison === x.raison} onClick={() => { setRaison(x.raison); setDecalage(0); }} testId={`groups-reason-${x.raison || "tout"}`}>{x.raison || "Toutes"} ({x.nombre})</Pill>
        ))}
      </div>
      {info.total > 0 && (
        <div className="flex items-center justify-between gap-2 text-sm" data-testid="groups-pager">
          <button type="button" className="at-btn at-btn-ghost" disabled={decalage === 0} onClick={() => setDecalage(Math.max(0, decalage - PAGE_GR))} data-testid="groups-prev">Précédents</button>
          <span data-testid="groups-range">{decalage + 1}–{fin} sur {info.total}</span>
          <button type="button" className="at-btn at-btn-ghost" disabled={fin >= info.total} onClick={() => setDecalage(decalage + PAGE_GR)} data-testid="groups-next">Suivants</button>
        </div>
      )}
      {liste.length === 0 && <Vide texte="Aucun regroupement à valider." testId="groups-empty" />}
      {liste.map((g) => (
        <div key={g.id} className="at-card p-4 space-y-2" data-testid={`group-${g.id}`}>
          <p className="font-bold">{g.raison} · {g.biens.length} biens</p>
          {g.biens.map((b) => <p key={b.id} className="text-sm at-soft px-3 py-2 break-words">{[b.prenom, b.nom].filter(Boolean).join(" ")} · {b.telephone} · {[b.adresse, b.code_postal, b.ville].filter(Boolean).join(" ")}</p>)}
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="at-btn at-btn-primary" onClick={() => traiter(g, true)} data-testid={`group-validate-${g.id}`}>Valider</button>
            <button type="button" className="at-btn at-btn-ghost" onClick={() => traiter(g, false)} data-testid={`group-reject-${g.id}`}>Rejeter</button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Prospection() {
  const [onglet, setOnglet] = useState("fichiers");
  const [ouvert, setOuvert] = useState(false);
  const [cle, setCle] = useState(0);
  return (
    <div data-testid="page-prospection" className="min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="page-title">Fichiers de prospection</h1>
        <button type="button" className="at-btn at-btn-primary" onClick={() => setOuvert(true)} data-testid="prospect-import-open"><Upload size={16} />Importer un fichier</button>
      </div>
      <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4">
        {[["fichiers", "Fichiers"], ["distribution", "Distribution"], ["regroupements", "Regroupements à valider"]].map(([k, l]) => <Pill key={k} actif={onglet === k} onClick={() => setOnglet(k)} testId={`prospect-tab-${k}`}>{l}</Pill>)}
      </div>
      {onglet === "fichiers" && <Fichiers cle={cle} />}
      {onglet === "distribution" && <Distribution />}
      {onglet === "regroupements" && <Regroupements />}
      <ImportProspection ouvert={ouvert} setOuvert={setOuvert} onFini={() => setCle((c) => c + 1)} />
    </div>
  );
}
